import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Star,
  MessageSquare,
  AlertCircle,
  ThumbsUp,
  Bug,
  Lightbulb,
  Clock,
  CheckCircle,
  Users,
  Award,
  Shield,
  HeartHandshake,
  Globe,
  Search as SearchIcon,
} from "lucide-react";
import moment from "moment-timezone";
import { submitContactRequest, submitFeedback } from "../apis/settings";
import { useTimezone } from "../contexts/TimezoneContext";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("timezone");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [feedbackForm, setFeedbackForm] = useState({
    name: "",
    email: "",
    rating: 0,
    feedback_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  // Timezone tab state
  const {
    timezone: accountTimezone,
    fallbackTimezone,
    canEdit: canEditTimezone,
    saving: timezoneSaving,
    availableTimezones,
    setTimezone: saveAccountTimezone,
  } = useTimezone();

  const [selectedTz, setSelectedTz] = useState(accountTimezone || fallbackTimezone);
  const [tzSearch, setTzSearch] = useState("");

  useEffect(() => {
    setSelectedTz(accountTimezone || fallbackTimezone);
  }, [accountTimezone, fallbackTimezone]);

  const filteredTimezones = useMemo(() => {
    const q = tzSearch.trim().toLowerCase();
    if (!q) return availableTimezones;
    return availableTimezones.filter(
      (t) =>
        t.name.toLowerCase().includes(q) || t.label.toLowerCase().includes(q)
    );
  }, [availableTimezones, tzSearch]);

  const handleSaveTimezone = async () => {
    if (!canEditTimezone) return;
    if (!selectedTz) {
      toast.error("Please pick a timezone");
      return;
    }
    await saveAccountTimezone(selectedTz);
  };

  const previewNow = useMemo(() => {
    const zone =
      selectedTz && moment.tz.zone(selectedTz)
        ? selectedTz
        : fallbackTimezone;
    return moment().tz(zone).format("MMM DD, YYYY hh:mm:ss A");
  }, [selectedTz, fallbackTimezone]);

  const companyInfo = {
    name: "Ping",
    phone: "+91 97277 89826",
    email: "sales@blutec.ai",
    address: "Ahmedabad, Gujarat India",
  };

  const stats = [
    {
      icon: Users,
      label: "Happy Customers",
      value: "10,000+",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
    },
    {
      icon: Clock,
      label: "Response Time",
      value: "< 2 Hours",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
    },
    {
      icon: Award,
      label: "Satisfaction Rate",
      value: "98%",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
    },
    {
      icon: Shield,
      label: "Data Security",
      value: "100%",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
    },
  ];

  const feedbackTypes = [
    {
      value: "suggestion",
      label: "Suggestion",
      icon: Lightbulb,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
    },
    {
      value: "complaint",
      label: "Complaint",
      icon: AlertCircle,
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
    },
    {
      value: "compliment",
      label: "Compliment",
      icon: ThumbsUp,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
    },
    {
      value: "bug_report",
      label: "Bug Report",
      icon: Bug,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
    },
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await submitContactRequest(contactForm);
      if (response.data.success) {
        toast.success(
          "Contact request submitted successfully! We'll get back to you soon."
        );
        setContactForm({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        toast.error(
          response.data.message || "Failed to submit contact request"
        );
      }
    } catch (error) {
      console.error("Contact submission error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await submitFeedback(feedbackForm);
      if (response.data.success) {
        toast.success(
          "Thank you for your feedback! Your input helps us improve."
        );
        setFeedbackForm({
          name: "",
          email: "",
          rating: 0,
          feedback_type: "",
          message: "",
        });
      } else {
        toast.error(response.data.message || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ rating, setRating }) => (
    <div className="flex space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <motion.div
          key={star}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.95 }}
        >
          <Star
            className={`w-8 h-8 cursor-pointer transition-all duration-200 ${
              star <= rating
                ? "text-amber-500 fill-amber-500"
                : "text-gray-300 hover:text-amber-400"
            }`}
            onClick={() => setRating(star)}
          />
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f3f3f3] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="flex justify-center mb-4">
            <div className="bg-blue-50 p-4 rounded-2xl shadow-sm border border-blue-200">
              <HeartHandshake className="w-10 h-10 text-blue-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2 text-gray-900">
            Settings & Support
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto font-medium">
            We're here to help you succeed. Connect with our team or share your
            valuable feedback.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02, y: -2 }}
                className={`rounded-2xl p-4 shadow-sm hover:shadow-md transition-all bg-white border ${stat.borderColor}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-1">
                      {stat.label}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor} border ${stat.borderColor}`}>
                    <IconComponent className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center mb-6"
        >
          <div className="bg-white rounded-2xl p-2 shadow-sm border border-gray-200 inline-flex gap-2 flex-wrap justify-center">
            <button
              onClick={() => setActiveTab("timezone")}
              className={`px-6 py-3 text-sm rounded-xl font-bold transition-all duration-300 flex items-center gap-2 ${
                activeTab === "timezone"
                  ? "text-gray-900 shadow-sm border border-gray-300"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              style={activeTab === "timezone" ? { backgroundColor: "#eefa94" } : {}}
            >
              <Globe className="w-4 h-4" />
              Timezone
            </button>
            <button
              onClick={() => setActiveTab("contact")}
              className={`px-6 py-3 text-sm rounded-xl font-bold transition-all duration-300 flex items-center gap-2 ${
                activeTab === "contact"
                  ? "text-gray-900 shadow-sm border border-gray-300"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              style={activeTab === "contact" ? { backgroundColor: "#eefa94" } : {}}
            >
              <Phone className="w-4 h-4" />
              Contact Us
            </button>
            <button
              onClick={() => setActiveTab("feedback")}
              className={`px-6 py-3 text-sm rounded-xl font-bold transition-all duration-300 flex items-center gap-2 ${
                activeTab === "feedback"
                  ? "text-gray-900 shadow-sm border border-gray-300"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              style={activeTab === "feedback" ? { backgroundColor: "#eefa94" } : {}}
            >
              <MessageSquare className="w-4 h-4" />
              Feedback
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-1"
          >
            <div className="rounded-2xl shadow-sm p-5 h-fit border border-gray-200 bg-white">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  Get in Touch
                </h2>
              </div>

              <div className="space-y-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-0.5">
                      Phone
                    </p>
                    <p className="font-bold text-sm text-gray-900">
                      {companyInfo.phone}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="p-2.5 rounded-xl bg-blue-50 border border-blue-200">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-0.5">
                      Email
                    </p>
                    <p className="font-bold text-sm text-gray-900">
                      {companyInfo.email}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="p-2.5 rounded-xl bg-purple-50 border border-purple-200">
                    <MapPin className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 mb-0.5">
                      Address
                    </p>
                    <p className="font-bold text-sm text-gray-900">
                      {companyInfo.address}
                    </p>
                  </div>
                </motion.div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700 leading-relaxed font-medium">
                      We're committed to providing exceptional support. Our team
                      typically responds within 2 hours during business hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-2"
          >
            <AnimatePresence mode="wait">
              {activeTab === "timezone" ? (
                <motion.div
                  key="timezone"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl shadow-sm p-5 border border-gray-200 bg-white"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                      <Globe className="w-5 h-5 text-blue-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Account Timezone
                    </h2>
                  </div>

                  <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                    All conversation and message timestamps in Live Chat
                    (including relative times like "5m ago" / "7h ago") will be
                    shown in this timezone. The database stores everything in
                    UTC — we only convert at display time. This setting is
                    shared by all agents on the account.
                  </p>

                  {!canEditTimezone && (
                    <div className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">
                        Only the account owner can change the timezone. Ask
                        them to update it if you need a different value.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="p-3 rounded-xl border border-gray-200 bg-gray-50">
                      <p className="text-xs font-bold text-gray-500 mb-1">
                        Current account timezone
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {accountTimezone || `${fallbackTimezone} (browser default)`}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-gray-200 bg-gray-50">
                      <p className="text-xs font-bold text-gray-500 mb-1">
                        Preview (selected timezone)
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {previewNow}
                      </p>
                    </div>
                  </div>

                  <div className="relative mb-3">
                    <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search timezone (e.g. Kolkata, New_York, UTC+05)"
                      value={tzSearch}
                      onChange={(e) => setTzSearch(e.target.value)}
                      disabled={!canEditTimezone}
                      className="w-full pl-9 pr-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Pick a timezone
                  </label>
                  <select
                    value={selectedTz || ""}
                    onChange={(e) => setSelectedTz(e.target.value)}
                    disabled={!canEditTimezone}
                    size={10}
                    className="w-full px-3 py-2 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {filteredTimezones.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.label}
                      </option>
                    ))}
                    {filteredTimezones.length === 0 && (
                      <option disabled>No matching timezones</option>
                    )}
                  </select>

                  <div className="flex items-center justify-between mt-5">
                    <p className="text-xs text-gray-500">
                      Showing {filteredTimezones.length} of {availableTimezones.length} timezones
                    </p>
                    <motion.button
                      type="button"
                      onClick={handleSaveTimezone}
                      disabled={
                        !canEditTimezone ||
                        timezoneSaving ||
                        selectedTz === (accountTimezone || fallbackTimezone)
                      }
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="text-gray-900 py-3 px-6 text-sm rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-sm hover:shadow-md border border-gray-300"
                      style={{ backgroundColor: "#eefa94" }}
                    >
                      <Globe className="w-4 h-4" />
                      <span>
                        {timezoneSaving ? "Saving..." : "Save timezone"}
                      </span>
                    </motion.button>
                  </div>
                </motion.div>
              ) : activeTab === "contact" ? (
                <motion.div
                  key="contact"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl shadow-sm p-5 border border-gray-200 bg-white"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                      <Send className="w-5 h-5 text-blue-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Contact Form
                    </h2>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          placeholder="John Doe"
                          value={contactForm.name}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              name: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Your Email *
                        </label>
                        <input
                          type="email"
                          placeholder="john@example.com"
                          value={contactForm.email}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              email: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          placeholder="+91 98765 43210"
                          value={contactForm.phone}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              phone: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Subject *
                        </label>
                        <input
                          type="text"
                          placeholder="How can we help?"
                          value={contactForm.subject}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              subject: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Your Message *
                      </label>
                      <textarea
                        placeholder="Tell us more about your inquiry..."
                        rows="5"
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            message: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all resize-none"
                        required
                      ></textarea>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full text-gray-900 py-4 px-6 text-sm rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md border border-gray-300"
                      style={{ backgroundColor: "#eefa94" }}
                    >
                      <Send className="w-5 h-5" />
                      <span>{loading ? "Sending..." : "Send Message"}</span>
                    </motion.button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  key="feedback"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-2xl shadow-sm p-5 border border-gray-200 bg-white"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-200">
                      <MessageSquare className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Share Your Feedback
                    </h2>
                  </div>

                  <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          placeholder="John Doe"
                          value={feedbackForm.name}
                          onChange={(e) =>
                            setFeedbackForm({
                              ...feedbackForm,
                              name: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Your Email *
                        </label>
                        <input
                          type="email"
                          placeholder="john@example.com"
                          value={feedbackForm.email}
                          onChange={(e) =>
                            setFeedbackForm({
                              ...feedbackForm,
                              email: e.target.value,
                            })
                          }
                          className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all"
                          required
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                      <label className="block text-sm font-bold mb-3 text-gray-900">
                        Rate your experience
                      </label>
                      <div className="flex justify-center">
                        <StarRating
                          rating={feedbackForm.rating}
                          setRating={(rating) =>
                            setFeedbackForm({ ...feedbackForm, rating })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold mb-3 text-gray-900">
                        Feedback Type *
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {feedbackTypes.map((type) => {
                          const IconComponent = type.icon;
                          const isActive =
                            feedbackForm.feedback_type === type.value;
                          return (
                            <motion.button
                              key={type.value}
                              type="button"
                              onClick={() =>
                                setFeedbackForm({
                                  ...feedbackForm,
                                  feedback_type: type.value,
                                })
                              }
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className={`p-4 border rounded-xl text-center transition-all ${
                                isActive
                                  ? `${type.borderColor} ${type.bgColor} shadow-sm`
                                  : "border-gray-200 bg-white hover:bg-gray-50"
                              }`}
                            >
                              <IconComponent
                                className={`w-6 h-6 mx-auto mb-2 ${
                                  isActive ? type.color : "text-gray-400"
                                }`}
                              />
                              <p className="text-xs font-bold text-gray-900">
                                {type.label}
                              </p>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Your Feedback *
                      </label>
                      <textarea
                        placeholder="Share your thoughts with us..."
                        rows="5"
                        value={feedbackForm.message}
                        onChange={(e) =>
                          setFeedbackForm({
                            ...feedbackForm,
                            message: e.target.value,
                          })
                        }
                        className="w-full px-4 py-3 text-sm font-medium rounded-xl border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 transition-all resize-none"
                        required
                      ></textarea>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full text-gray-900 py-4 px-6 text-sm rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md border border-gray-300"
                      style={{ backgroundColor: "#eefa94" }}
                    >
                      <MessageSquare className="w-5 h-5" />
                      <span>{loading ? "Submitting..." : "Submit Feedback"}</span>
                    </motion.button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
